import React, { useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getPhaseConfig } from './PhaseChip';

const PHASE_ORDER = ['accumulation', 'markup', 'distribution', 'absorption'];

export default function PhaseTimeline({ history }) {
  const { t } = useTranslation();
  const data = useMemo(() => {
    if (!history || history.length === 0) return [];
    return history.map((h) => ({
      date: h.date,
      phase: h.phase,
      confidence: h.confidence,
      config: getPhaseConfig(h.phase),
    }));
  }, [history]);

  if (data.length === 0) return null;

  // Group consecutive same-phase blocks
  const blocks = [];
  let current = { phase: data[0].phase, start: 0, end: 0, config: data[0].config };
  for (let i = 1; i < data.length; i++) {
    if (data[i].phase === current.phase) {
      current.end = i;
    } else {
      blocks.push({
        ...current,
        startDate: data[current.start].date,
        endDate: data[current.end].date,
        days: current.end - current.start + 1,
      });
      current = { phase: data[i].phase, start: i, end: i, config: data[i].config };
    }
  }
  blocks.push({
    ...current,
    startDate: data[current.start].date,
    endDate: data[current.end].date,
    days: current.end - current.start + 1,
  });

  const totalDays = data.length;

  return (
    <Box sx={{
      background: '#fff',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      p: 2.5,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
          {t('phase.timeline.title')}
        </Typography>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8' }}>
          {t('phase.timeline.days', { count: totalDays })}
        </Typography>
      </Box>

      {/* Timeline bar */}
      <Box sx={{
        display: 'flex',
        borderRadius: '8px',
        overflow: 'hidden',
        height: 36,
        mb: 2,
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)',
      }}>
        {blocks.map((block, i) => {
          const widthPct = (block.days / totalDays * 100).toFixed(1);
          return (
            <Tooltip
              key={i}
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{t(`phase.phases.${block.phase}`)}</Typography>
                  <Typography sx={{ fontSize: 11, opacity: 0.8 }}>
                    {block.startDate} → {block.endDate}
                  </Typography>
                  <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{t('phase.timeline.sessions', { count: block.days })}</Typography>
                </Box>
              }
              arrow
            >
              <Box
                sx={{
                  width: `${widthPct}%`,
                  minWidth: 3,
                  background: block.config.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'opacity 0.15s, filter 0.15s',
                  '&:hover': { opacity: 0.85, filter: 'brightness(1.1)' },
                  borderRight: i < blocks.length - 1 ? '2px solid #fff' : 'none',
                }}
              >
                {block.days >= 8 && (
                  <Typography sx={{
                    color: 'white',
                    fontSize: '0.62rem',
                    fontWeight: 800,
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                  }}>
                    {t('phase.timeline.dayShort', { count: block.days })}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
        {PHASE_ORDER.map((phase) => {
          const config = getPhaseConfig(phase);
          const days = blocks.filter(b => b.phase === phase).reduce((s, b) => s + b.days, 0);
          if (days === 0) return null;
          return (
            <Box key={phase} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{
                width: 10,
                height: 10,
                borderRadius: '3px',
                background: config.gradient,
              }} />
              <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600 }}>
                {t('phase.timeline.legend', { label: t(`phase.phases.${phase}`), days, pct: (days / totalDays * 100).toFixed(0) })}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Confidence sparkline */}
      {data.length > 1 && (
        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{
            fontSize: '0.68rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '#94a3b8',
            mb: 1,
          }}>
            {t('phase.timeline.confidence')}
          </Typography>
          <Box sx={{
            display: 'flex',
            height: 32,
            alignItems: 'flex-end',
            gap: '1px',
            backgroundColor: '#fafbfc',
            borderRadius: '6px',
            p: 0.5,
            border: '1px solid #f1f5f9',
          }}>
            {data.map((d, i) => {
              const h = Math.max(3, (d.confidence || 0) * 28);
              return (
                <Tooltip key={i} title={`${d.date}: ${(d.confidence * 100).toFixed(0)}%`} arrow>
                  <Box sx={{
                    flex: 1,
                    height: h,
                    borderRadius: '2px',
                    background: d.config.gradient,
                    opacity: 0.7,
                    transition: 'opacity 0.15s',
                    '&:hover': { opacity: 1 },
                    cursor: 'crosshair',
                  }} />
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}
